import is from '@sindresorhus/is';
import type { UseMutationResult } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { HttpFetcherError } from '~/shared/http-fetcher/http-fetcher';

import type { CompanyInformationGenerationResponse } from '../../data-contract/company-information-generation';
import { useApplicationFetchers } from '../../fetchers/use-application-fetchers';
import { jobArchitectureKeys } from './query-keys';

type SubmitGenerationParams = {
    readonly companyDomain: string;
    readonly companyName: string;
    readonly jobArchitectureId: string;
    readonly regenerate: boolean;
    readonly signal?: AbortSignal;
};

export type UseSubmitCompanyInformationGenerationResult = UseMutationResult<
    CompanyInformationGenerationResponse,
    HttpFetcherError,
    SubmitGenerationParams
>;

export const useSubmitCompanyInformationGeneration = (): UseSubmitCompanyInformationGenerationResult => {
    const { submitCompanyInformationGeneration } = useApplicationFetchers();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (params: SubmitGenerationParams) => {
            const domainToSend = is.emptyStringOrWhitespace(params.companyDomain) ? null : params.companyDomain.trim();
            const result = await submitCompanyInformationGeneration({
                jobArchitectureId: params.jobArchitectureId,
                payload: {
                    companyDomain: domainToSend,
                    companyName: params.companyName,
                },
                regenerate: params.regenerate,
                signal: params.signal,
            });

            if (result.isErr) {
                throw result.error;
            }

            return result.value;
        },
        networkMode: 'always',
        onSuccess: (_data, variables) => {
            void queryClient.invalidateQueries({
                queryKey: jobArchitectureKeys.companyInformationGeneration(variables.jobArchitectureId),
            });
        },
        throwOnError: false,
    });
};
